import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: 0, title: "Business basics", subtitle: "Tell us who you are" },
  { id: 1, title: "Audience & competition", subtitle: "Who you serve, who you compete with" },
  { id: 2, title: "Goals & brand", subtitle: "Where you're heading, how you sound" },
];

export default function Onboarding() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    business_category: "",
    industry: "",
    business_description: "",
    website: "",
    phone: "",
    country: "",
    city: "",
    target_audience: "",
    competitors: "",
    brand_color: "#6366F1",
    business_goals: "",
    languages: ["English"],
    marketing_budget: "",
    preferred_tone: "Professional",
    posting_frequency: "Daily",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isStepValid = () => {
    if (step === 0)
      return form.business_name && form.business_category && form.industry && form.business_description && form.country;
    if (step === 1) return form.target_audience;
    if (step === 2) return form.business_goals;
    return true;
  };

  const submit = async () => {
    if (!isStepValid()) {
      toast.error("Please fill in required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/business/onboard", form);
      await refresh();
      toast.success("Brand onboarded — let's build.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg p-6 lg:p-12">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-9 w-9 rounded-lg brand-gradient grid place-items-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight">
            MakeYour<span className="brand-gradient-text">Brand.IN</span>
          </span>
        </div>

        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-3">
            Step {step + 1} of {STEPS.length}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">{STEPS[step].title}</h1>
          <p className="text-white/50 mt-2">{STEPS[step].subtitle}</p>

          <div className="mt-6 flex gap-2">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "brand-gradient" : "bg-white/10"}`}
              />
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-7 lg:p-10 space-y-5">
          {step === 0 && (
            <>
              <Field label="Business name *" testid="onb-business-name">
                <input
                  value={form.business_name}
                  onChange={set("business_name")}
                  data-testid="onb-business-name"
                  className={inputCls}
                  placeholder="Saanvi Studio"
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Category *">
                  <input
                    value={form.business_category}
                    onChange={set("business_category")}
                    data-testid="onb-category"
                    className={inputCls}
                    placeholder="Fashion / SaaS / Restaurant…"
                  />
                </Field>
                <Field label="Industry *">
                  <input
                    value={form.industry}
                    onChange={set("industry")}
                    data-testid="onb-industry"
                    className={inputCls}
                    placeholder="Apparel & Lifestyle"
                  />
                </Field>
              </div>
              <Field label="Business description *">
                <textarea
                  rows={3}
                  value={form.business_description}
                  onChange={set("business_description")}
                  data-testid="onb-description"
                  className={inputCls}
                  placeholder="A 2-3 sentence description of what your business does…"
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Website (optional)">
                  <input value={form.website} onChange={set("website")} data-testid="onb-website" className={inputCls} placeholder="https://…" />
                </Field>
                <Field label="Phone (optional)">
                  <input value={form.phone} onChange={set("phone")} data-testid="onb-phone" className={inputCls} placeholder="+91 9xxxxxxxxx" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Country *">
                  <input value={form.country} onChange={set("country")} data-testid="onb-country" className={inputCls} placeholder="India" />
                </Field>
                <Field label="City">
                  <input value={form.city} onChange={set("city")} data-testid="onb-city" className={inputCls} placeholder="Mumbai" />
                </Field>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <Field label="Who is your target audience? *">
                <textarea
                  rows={3}
                  value={form.target_audience}
                  onChange={set("target_audience")}
                  data-testid="onb-audience"
                  className={inputCls}
                  placeholder="Young professionals aged 25-40 in metro cities who value sustainable fashion…"
                />
              </Field>
              <Field label="Top competitors (comma separated)">
                <input
                  value={form.competitors}
                  onChange={set("competitors")}
                  data-testid="onb-competitors"
                  className={inputCls}
                  placeholder="Brand A, Brand B, Brand C"
                />
              </Field>
              <Field label="Marketing budget (monthly)">
                <select value={form.marketing_budget} onChange={set("marketing_budget")} data-testid="onb-budget" className={inputCls}>
                  <option value="">Select…</option>
                  <option>Under $500</option>
                  <option>$500 - $2,000</option>
                  <option>$2,000 - $10,000</option>
                  <option>$10,000+</option>
                </select>
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Business goals *">
                <textarea
                  rows={3}
                  value={form.business_goals}
                  onChange={set("business_goals")}
                  data-testid="onb-goals"
                  className={inputCls}
                  placeholder="Reach 10k Instagram followers, launch a D2C site, generate 200 qualified leads per month…"
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Preferred tone">
                  <select value={form.preferred_tone} onChange={set("preferred_tone")} data-testid="onb-tone" className={inputCls}>
                    <option>Professional</option>
                    <option>Friendly</option>
                    <option>Bold</option>
                    <option>Luxury</option>
                    <option>Playful</option>
                    <option>Inspirational</option>
                  </select>
                </Field>
                <Field label="Posting frequency">
                  <select value={form.posting_frequency} onChange={set("posting_frequency")} data-testid="onb-frequency" className={inputCls}>
                    <option>Daily</option>
                    <option>3x per week</option>
                    <option>Weekly</option>
                    <option>Bi-weekly</option>
                  </select>
                </Field>
              </div>
              <Field label="Brand color">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.brand_color}
                    onChange={set("brand_color")}
                    data-testid="onb-brand-color"
                    className="h-12 w-16 rounded-lg bg-transparent border border-white/10 cursor-pointer"
                  />
                  <span className="font-mono text-sm text-white/60">{form.brand_color}</span>
                </div>
              </Field>
            </>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button
            data-testid="onb-prev"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm glass hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              data-testid="onb-next"
              onClick={() => isStepValid() ? setStep(step + 1) : toast.error("Fill required fields")}
              className="inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              data-testid="onb-submit"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange disabled:opacity-50"
            >
              {loading ? "Saving…" : (
                <>
                  Launch my brand <Check className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
